
import { Injectable } from "@angular/core";
import { BehaviorSubject, fromEvent, interval, merge, Observable, of, race, Subject, Subscription, timer } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";
import { v4 } from 'uuid';


class BroadcastSubject<T> extends Subject<T> {

    private _subscriptions: Subscription[] = []
    constructor(
        private readonly _bc: LVBroadcastChannel<any>,
        private readonly _messageType: string
    ) {
        super();

        this._subscriptions.push(
            this._bc.listen(this._messageType)
                .subscribe((value) => this.next(value, true))
        )




    }

    override next(value: T, fromBC: boolean = false) {
        if (!fromBC) {
            this._bc.send({ messageType: this._messageType, payload: value })
        }
        super.next(value);
    }

    override complete(): void {
        this._subscriptions.forEach((s) => s.unsubscribe())
        super.complete()
    }
}

type LVBroadcastMessage<T extends any, K extends keyof T = keyof T> = {
    messageType: K,
    payload?: T[K]
}

class LVBroadcastChannel<T extends { [messageType: string]: any }> {

    readonly #bc: BroadcastChannel;
    readonly #message$: Observable<LVBroadcastMessage<T>>;
    readonly #error$: Observable<any>
    readonly takenOver = new Subject<void>();

    constructor(
        public name: string,
        public log: boolean = false
    ) {

        this.#bc = new BroadcastChannel(name);
        this.#message$ = fromEvent(this.#bc, 'message').pipe(
            map((event: MessageEvent<LVBroadcastMessage<T>>) => event.data)
        );
        this.#error$ = fromEvent(this.#bc, 'error');
    }

    send<K extends keyof T>(message: LVBroadcastMessage<T, K>) {
        this.#bc.postMessage(message);
    }

    listen<K extends keyof T>(messageType: K) {
        return this.#message$.pipe(
            filter((message) => message.messageType === messageType),
            map(({ payload }) => payload)
        )
    }

    listenErrors() {
        return this.#error$
    }

}



type BroadcastChannelModel = {
    ping: {
        ping: void,
        answerPing: void,
        rootDestroyed: void,
        newRootCreated: string
    }
}



type BroadcastChannelInit = Record<string, () => Observable<any>>


type InferObsReturn<T extends (...params: any) => Observable<any>> = T extends (...params: any) => Observable<infer U> ? () => Observable<U> :
    T extends (...params: unknown[]) => Subject<infer U> ? () => Observable<U> :
    T extends (...params: unknown[]) => BehaviorSubject<infer U> ? () => Observable<U> :
    never

type InferFromObservablesReturn<T extends BroadcastChannelInit> = {
    [K in keyof T]: InferObsReturn<T[K]>
}


@Injectable({ providedIn: 'root' })
export class BroadcastChannelService {

    readonly PING_INTERVAL = 5;


    readonly #bcuuid = v4();
    get rootBC() {
        return localStorage.getItem('rootBC')
    }
    get amIRoot() {
        return this.#bcuuid === this.rootBC;
    }

    readonly #amIRootSubject = new BehaviorSubject<boolean>(this.amIRoot);


    readonly #pingBC = new LVBroadcastChannel<BroadcastChannelModel['ping']>('ping');
    get pingBc() {
        return this.#pingBC;
    }

    readonly #subjectBC = new LVBroadcastChannel(null);

    readonly #broadcastChannelRepo: { [key: string]: LVBroadcastChannel<any> } = {
        ping: this.#pingBC,
        subjectBC: this.#subjectBC
    };





    readonly #unloaded$ = merge(
        fromEvent(window, 'beforeunload'),
        fromEvent(window, 'unload')
    )
        .pipe(
            take(1)
        )

    readonly rootExists$ = interval(this.PING_INTERVAL * 1000).pipe(
        switchMap(() => {
            const obs = race(
                interval(this.PING_INTERVAL / 2 * 1000).pipe(take(1), map(() => false)),
                this.#pingBC.listen('answerPing').pipe(take(1), map(() => true))
            )
            this.pingBc.send({ messageType: 'ping' })
            return obs;
        })
    )

    constructor() {

        if (!this.rootBC) {
            this.#takeOver()
        }

        this.#unloaded$.subscribe(() => {
            if (this.amIRoot) {
                this.#pingBC.send({ messageType: 'rootDestroyed' })
            }
        })


        this.#pingBC.listen('rootDestroyed')
            .pipe(
                switchMap(() => merge(
                    timer(Math.random() * 1000).pipe(map(() => true)),
                    this.#pingBC.listen('newRootCreated').pipe(map(() => false))
                ).pipe(
                    take(1)
                ))
            )
            .subscribe((takeOver) => {
                if (takeOver) {
                    this.#takeOver()
                }
            })




        this.#amIRootSubject.pipe(
            switchMap((amIRoot) => amIRoot ? of(true) : this.rootExists$),
            filter(x => !x)
        )
            .subscribe(() => {

                this.#takeOver()
            })



        this.#amIRootSubject.pipe(
            filter(Boolean),
            switchMap(() => this.#pingBC.listen('ping'))
        )
            .subscribe(() => {

                this.#pingBC.send({ messageType: 'answerPing' })
            })

    }


    #getBroadcastChannel(broadcastChannelName: string) {
        return this.#broadcastChannelRepo[broadcastChannelName]
    }

    createBroadcastChannel$<T extends BroadcastChannelInit>(broadcastChannelName: string, initObj: T) {
        const bc = new LVBroadcastChannel<T>(broadcastChannelName, true);
        this.#broadcastChannelRepo[broadcastChannelName] = bc;
        return this.fromObservables(broadcastChannelName, initObj);
    }


    readonly #fromObservableFn = (broadcastChannelName: string, messageType: string, fn: () => Observable<any>) => () =>
        this.#amIRootSubject.pipe(
            switchMap((amIRoot) => {
                if (amIRoot) {
                    return fn().pipe(
                        tap((value) => {
                            this.#getBroadcastChannel(broadcastChannelName).send({ messageType, payload: value })
                        })
                    )
                }

                return this.#getBroadcastChannel(broadcastChannelName).listen(messageType)
            })
        )

    readonly fromObservables = <T extends BroadcastChannelInit>(broadcastChannelName: string, obj: T) => {

        const entries = Object.entries(obj).map(([messageType, fn]) => [messageType, this.#fromObservableFn(broadcastChannelName, messageType, fn)] as const)

        return Object.fromEntries(entries) as InferFromObservablesReturn<T>
    }



    readonly createBroadcastSubject = <T extends any>(name: string, until?: Observable<any>) => {
        const subject = new BroadcastSubject<T>(this.#subjectBC, name);

        if (until) {
            until.pipe(take(1))
                .subscribe(() => {
                    subject.complete();
                })
        }
        return subject
    }



    #takeOver() {
        window.localStorage.setItem('rootBC', this.#bcuuid);
        this.#pingBC.send({ messageType: 'newRootCreated', payload: this.#bcuuid })
        this.#amIRootSubject.next(true);
    }


}

